import React from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import clsx from 'clsx';
import useModal from '@/hooks/useModal';
import { ModalHeaderArrowBack } from '@/components/modals/ModalHeaderArrowBack';
import { awsService, casesLawyerService, GET_CASE_STEPS_STATUS } from '@/api';
import { CreateDocumentsToSend, FieldConfig } from '@/interfaces';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { useQueryClient } from '@tanstack/react-query';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import SendDocumentForm from '@/components/screens/lawyerapp/cases/case-clients/send-document/SendDocumentForm';
import { ConfigureFieldsStep } from '@/components/screens/lawyerapp/cases/case-clients/send-document/configure-fields';
import { countPdfFields } from '@/components/screens/lawyerapp/cases/case-clients/send-document/configure-fields/lib/countPdfFields';
import { TemplatePreview } from './TemplatePreview';

interface Props {
	caseId: string;
	open: boolean;
	documentsPreviewData: Partial<CreateDocumentsToSend>[];
	refetch: (data: CreateDocumentsToSend) => void;
	handleClose: () => void;
	handlePreviewData: (data: CreateDocumentsToSend) => void;
	handleApprove: (type: 'Retainer' | 'Document') => void;
}

export const DocumentContent: React.FC<Props> = ({
	caseId,
	open,
	refetch,
	handleClose,
	documentsPreviewData,
	handlePreviewData,
	handleApprove,
}) => {
	const { toastError } = useNotification();
	const {
		Modal: ModalDocument,
		show: showModalDocument,
		hide: hideModalDocument,
	} = useModal();

	const { ModalPreviewPage, showPreviewPage } = useModalPreviewPage();

	const queryClient = useQueryClient();

	const [file, setFile] = React.useState<File | null>(null);
	const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
	const [fieldConfigs, setFieldConfigs] = React.useState<
		Record<string, FieldConfig>
	>({});
	const [isLoading, setIsLoading] = React.useState(false);
	const [step, setStep] = React.useState<
		'upload' | 'configure-fields' | 'email'
	>('upload');

	const {
		register,
		handleSubmit,
		watch,
		formState,
		reset,
		setValue,
		getValues,
	} = useForm({ mode: 'onSubmit' });

	React.useEffect(() => {
		if (open) {
			showModalDocument();
		}
	}, [open]);

	React.useEffect(() => {
		if (step !== 'configure-fields' || !file) {
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setPdfUrl(objectUrl);

		return () => {
			URL.revokeObjectURL(objectUrl);
			setPdfUrl(null);
		};
	}, [step, file]);

	const onClose = (): void => {
		hideModalDocument();
		reset({});
		setFile(null);
		setFieldConfigs({});
		setStep('upload');
		handleClose();
	};

	const handleBack = (): void => {
		if (step === 'email') {
			setStep('configure-fields');
		} else if (step === 'configure-fields') {
			setStep('upload');
		}
	};

	const [isCheckingFields, setIsCheckingFields] = React.useState(false);
	const [hasFieldsHint, setHasFieldsHint] = React.useState<boolean>();

	const handleContinue = async () => {
		if (!file) return;
		setIsCheckingFields(true);
		const fieldCount = await countPdfFields(file);
		setIsCheckingFields(false);
		setHasFieldsHint(fieldCount === null ? undefined : fieldCount > 0);
		setStep('configure-fields');
	};

	React.useEffect(() => {
		setFieldConfigs({});
		setHasFieldsHint(undefined);
	}, [file]);

	const handleContinueFromFields = (
		configs: Record<string, FieldConfig>
	): void => {
		setFieldConfigs(configs);
		setStep('email');
	};

	const getFormattedData = (data: FieldValues): CreateDocumentsToSend => {
		const questions = Object.values(fieldConfigs).map((config, index) => ({
			field_name: config.fieldName,
			question: config.renamedLabel,
			type: config.dataType,
			order: index,
		}));

		return {
			case_id: caseId,
			name: data.document_name,
			email_text: data.message,
			is_signature_required: Object.values(fieldConfigs).some(
				(config) => config.dataType === 'SIGNATURE'
			),
			doc_url: '',
			questions,
			hideSigningDate: false,
		};
	};

	const onSubmit = async (data: FieldValues): Promise<void> => {
		if (!file) {
			toastError('Please upload the document');
			return;
		}

		setIsLoading(true);
		try {
			const payload = getFormattedData(data);

			const fileName = data.document_name.endsWith('.pdf')
				? data.document_name
				: `${data.document_name}.pdf`;
			const pdfFile = new File([file], fileName, { type: 'application/pdf' });
			const url = (await awsService.uploadImage(pdfFile)).data.data.location;

			const finalPayload = { ...payload, doc_url: url };

			await casesLawyerService.saveDocument(finalPayload);
			refetch(finalPayload);
			handleApprove('Document');
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
			onClose();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const handlePreview = (): void => {
		const data = getValues();
		const payload = getFormattedData(data);

		handlePreviewData(payload);
		showPreviewPage();
	};

	const isConfigureFieldsStep = step === 'configure-fields';

	return (
		<>
			<ModalDocument
				avoidClose
				backgroundColor="white"
				title={
					isConfigureFieldsStep ? undefined : (
						<ModalHeaderArrowBack
							title={
								step === 'email' ? 'Add Email Language' : 'Configure Document'
							}
							handleClose={onClose}
							handleBack={handleBack}
							showBack={step === 'email'}
						/>
					)
				}
				classNameCardContainer={clsx(
					'flex flex-col !transition-[opacity,transform]',
					{
						'documents-to-send-modal-panel': !isConfigureFieldsStep,
						'!p-0 !shadow-none !bg-transparent !rounded-none w-auto':
							isConfigureFieldsStep,
					}
				)}
				classNameContainerChildren={
					isConfigureFieldsStep
						? '!px-0 w-auto min-h-0'
						: 'w-full md:w-[520px] min-h-0'
				}
				noMaxHeight
				className={isConfigureFieldsStep ? undefined : 'overflow-y-hidden'}
			>
				<style>{`
					.documents-to-send-modal-panel {
						max-height: 93vh;
					}
					@supports (height: 100dvh) {
						.documents-to-send-modal-panel {
							max-height: 93dvh;
						}
					}
				`}</style>
				{isConfigureFieldsStep ? (
					file && pdfUrl ? (
						<ConfigureFieldsStep
							pdfUrl={pdfUrl}
							file={file}
							onBack={() => setStep('upload')}
							onClose={onClose}
							onContinue={handleContinueFromFields}
							initialFieldConfigs={fieldConfigs}
							hasFieldsHint={hasFieldsHint}
						/>
					) : null
				) : (
					<form onSubmit={handleSubmit(onSubmit)} className="h-full">
						<SendDocumentForm
							isLoading={isLoading || isCheckingFields}
							file={file}
							register={register}
							setFile={setFile}
							showPreviewPage={handlePreview}
							formState={formState}
							hideModalUploadDocument={hideModalDocument}
							handleContinue={handleContinue}
							setValue={setValue}
							watch={watch}
							showMessageInput={step === 'email'}
							submitLabel="Add Document"
						/>
					</form>
				)}
			</ModalDocument>
			<ModalPreviewPage
				handleClickBack={() => {
					showModalDocument();
				}}
				hideApproveButton
				hideEditButton={true}
				hideTitle={true}
				approveCustomLabel="Send Document"
			>
				<TemplatePreview documentsPreviewData={documentsPreviewData} />
			</ModalPreviewPage>
		</>
	);
};
